/**
 * Discussion Firestore Service - Firestore Integration for Discussion Messages
 *
 * This service provides Firestore persistence for discussion group messages,
 * supporting real-time multi-user discussions for collaborative trip planning.
 *
 * Data Model:
 * - Path: users/{effectiveUserId}/trips/{tripId}/discussion_messages/{messageId}
 * - Messages are stored as flat collection (no threads subcollection yet)
 * - Each message includes sender info, timestamp, content, and optional metadata
 *
 * effectiveUserId Pattern:
 * - For shared trips, the viewer's currentUser.uid != the data owner's UID
 * - Caller must pass effectiveUserId (from TripContext: tripOwnerId || currentUser.uid)
 * - This ensures messages are stored under the trip owner's Firestore path
 * - See CLAUDE.md "Shared Trip Data Access Pattern" for complete explanation
 *
 * Key Responsibilities:
 * - CRUD operations for discussion messages
 * - Pagination support for message history
 * - Real-time listener setup helpers
 * - Forum statistics and analytics
 */

import { db } from '@trip-planner-monorepo/shared';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  startAfter,
  where,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';

/**
 * Posts a new message to the discussion forum
 *
 * @param {string} userId - The effectiveUserId (trip owner for shared trips)
 * @param {string} tripId - Trip identifier
 * @param {Object} messageData - Message content and metadata
 * @param {string} messageData.senderId - User ID or 'AI_AGENT'
 * @param {string} messageData.senderName - Display name (snapshot at write time)
 * @param {string} messageData.text - The visible message content
 * @param {string} [messageData.senderAvatar] - URL to avatar
 * @param {string} [messageData.type='text'] - Message type ('text', 'proposal', 'system')
 * @param {Object} [messageData.proposal] - Proposal data (if type is 'proposal')
 * @param {Object} [messageData.metadata] - Additional metadata
 * @returns {Promise<string>} The ID of the newly created message
 * @throws {Error} If message creation fails
 */
export const postMessage = async (userId, tripId, messageData) => {
  try {
    // Validate required fields
    if (!userId || !tripId) {
      throw new Error('userId and tripId are required');
    }
    if (!messageData.senderId || !messageData.senderName || !messageData.text) {
      throw new Error('senderId, senderName, and text are required');
    }

    const messagesRef = collection(db, 'users', userId, 'trips', tripId, 'discussion_messages');

    const messageDoc = {
      // Core message fields
      text: messageData.text,
      senderId: messageData.senderId,
      senderName: messageData.senderName,
      senderAvatar: messageData.senderAvatar || null,
      timestamp: serverTimestamp(), // Use server timestamp for consistency

      // Message type and proposal data
      type: messageData.type || 'text',
      proposal: messageData.proposal || null,

      // Additional metadata
      metadata: messageData.metadata || {},

      // For future RAG/embedding support
      embedding: null
    };

    const docRef = await addDoc(messagesRef, messageDoc);

    console.log(`[discussionFirestoreService] Message posted: ${docRef.id} by ${messageData.senderId}`);
    return docRef.id;

  } catch (error) {
    console.error('[discussionFirestoreService] Error posting message:', error);
    throw error;
  }
};

/**
 * Retrieves messages with pagination support
 *
 * @param {string} userId - The effectiveUserId (trip owner for shared trips)
 * @param {string} tripId - Trip identifier
 * @param {Object} [options={}] - Query options
 * @param {number} [options.limit=50] - Maximum number of messages to retrieve
 * @param {Object} [options.startAfter] - Last document from previous query (for pagination)
 * @param {string} [options.orderBy='timestamp'] - Field to order by
 * @param {string} [options.orderDirection='asc'] - Order direction ('asc' or 'desc')
 * @param {string} [options.senderId] - Filter by specific sender
 * @returns {Promise<Object>} Object containing messages array, lastDoc, and hasMore flag
 * @throws {Error} If query fails
 */
export const getMessages = async (userId, tripId, options = {}) => {
  try {
    if (!userId || !tripId) {
      throw new Error('userId and tripId are required');
    }

    const {
      limit: queryLimit = 50,
      startAfter: lastDoc = null,
      orderBy: orderField = 'timestamp',
      orderDirection = 'asc',
      senderId = null
    } = options;

    const messagesRef = collection(db, 'users', userId, 'trips', tripId, 'discussion_messages');

    // Build query
    let q = query(
      messagesRef,
      orderBy(orderField, orderDirection),
      limit(queryLimit)
    );

    // Add sender filter if specified
    if (senderId) {
      q = query(q, where('senderId', '==', senderId));
    }

    // Add pagination cursor if provided
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const querySnapshot = await getDocs(q);

    const messages = querySnapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data(),
      // Convert Firestore Timestamp to ISO string for easier handling
      timestamp: docSnap.data().timestamp?.toDate?.()?.toISOString() || null
    }));

    const lastDocSnapshot = querySnapshot.docs[querySnapshot.docs.length - 1];
    const hasMore = querySnapshot.docs.length === queryLimit;

    console.log(`[discussionFirestoreService] Retrieved ${messages.length} messages for trip ${tripId}`);

    return {
      messages,
      lastDoc: lastDocSnapshot,
      hasMore
    };

  } catch (error) {
    console.error('[discussionFirestoreService] Error getting messages:', error);
    throw error;
  }
};

/**
 * Retrieves a single message by ID
 *
 * @param {string} userId - The effectiveUserId (trip owner for shared trips)
 * @param {string} tripId - Trip identifier
 * @param {string} messageId - Message identifier
 * @returns {Promise<Object>} Message data with ID
 * @throws {Error} If message not found or query fails
 */
export const getMessage = async (userId, tripId, messageId) => {
  try {
    if (!userId || !tripId || !messageId) {
      throw new Error('userId, tripId, and messageId are required');
    }

    const messageRef = doc(db, 'users', userId, 'trips', tripId, 'discussion_messages', messageId);
    const messageDoc = await getDoc(messageRef);

    if (!messageDoc.exists()) {
      throw new Error(`Message not found: ${messageId}`);
    }

    return {
      id: messageDoc.id,
      ...messageDoc.data(),
      timestamp: messageDoc.data().timestamp?.toDate?.()?.toISOString() || null
    };

  } catch (error) {
    console.error('[discussionFirestoreService] Error getting message:', error);
    throw error;
  }
};

/**
 * Updates an existing message
 * Used for editing message content or updating proposal status
 *
 * @param {string} userId - The effectiveUserId (trip owner for shared trips)
 * @param {string} tripId - Trip identifier
 * @param {string} messageId - Message identifier
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 * @throws {Error} If update fails
 */
export const updateMessage = async (userId, tripId, messageId, updates) => {
  try {
    if (!userId || !tripId || !messageId) {
      throw new Error('userId, tripId, and messageId are required');
    }

    const messageRef = doc(db, 'users', userId, 'trips', tripId, 'discussion_messages', messageId);

    // Add editedAt timestamp if content is being modified
    const updateData = {
      ...updates
    };

    if (updates.text) {
      updateData.editedAt = serverTimestamp();
    }

    await updateDoc(messageRef, updateData);

    console.log(`[discussionFirestoreService] Message updated: ${messageId}`);

  } catch (error) {
    console.error('[discussionFirestoreService] Error updating message:', error);
    throw error;
  }
};

/**
 * Deletes a message (soft delete)
 * Sets isDeleted flag to true rather than removing the document
 * This preserves message history and thread continuity
 *
 * @param {string} userId - The effectiveUserId (trip owner for shared trips)
 * @param {string} tripId - Trip identifier
 * @param {string} messageId - Message identifier
 * @returns {Promise<boolean>} Success status
 * @throws {Error} If deletion fails
 */
export const deleteMessage = async (userId, tripId, messageId) => {
  try {
    if (!userId || !tripId || !messageId) {
      throw new Error('userId, tripId, and messageId are required');
    }

    const messageRef = doc(db, 'users', userId, 'trips', tripId, 'discussion_messages', messageId);

    // Soft delete - preserve message for thread continuity
    await updateDoc(messageRef, {
      isDeleted: true,
      deletedAt: serverTimestamp(),
      // Clear sensitive content but keep structure
      text: '[Message deleted]'
    });

    console.log(`[discussionFirestoreService] Message soft-deleted: ${messageId}`);
    return true;

  } catch (error) {
    console.error('[discussionFirestoreService] Error deleting message:', error);
    throw error;
  }
};

/**
 * Hard deletes a message (permanent removal)
 * Only use for spam, abuse, or GDPR compliance
 *
 * @param {string} userId - The effectiveUserId (trip owner for shared trips)
 * @param {string} tripId - Trip identifier
 * @param {string} messageId - Message identifier
 * @returns {Promise<boolean>} Success status
 * @throws {Error} If deletion fails
 */
export const hardDeleteMessage = async (userId, tripId, messageId) => {
  try {
    if (!userId || !tripId || !messageId) {
      throw new Error('userId, tripId, and messageId are required');
    }

    const messageRef = doc(db, 'users', userId, 'trips', tripId, 'discussion_messages', messageId);
    await deleteDoc(messageRef);

    console.log(`[discussionFirestoreService] Message hard-deleted: ${messageId}`);
    return true;

  } catch (error) {
    console.error('[discussionFirestoreService] Error hard-deleting message:', error);
    throw error;
  }
};

/**
 * Retrieves forum statistics for a trip
 *
 * @param {string} userId - The effectiveUserId (trip owner for shared trips)
 * @param {string} tripId - Trip identifier
 * @returns {Promise<Object>} Statistics including totalMessages and activeParticipants
 * @throws {Error} If query fails
 */
export const getForumStats = async (userId, tripId) => {
  try {
    if (!userId || !tripId) {
      throw new Error('userId and tripId are required');
    }

    const messagesRef = collection(db, 'users', userId, 'trips', tripId, 'discussion_messages');
    const querySnapshot = await getDocs(messagesRef);

    // Calculate statistics
    const totalMessages = querySnapshot.size;
    const senderIds = new Set();
    const messageTypes = {};
    const recentActivity = [];

    querySnapshot.docs.forEach(docSnap => {
      const data = docSnap.data();

      // Track unique participants
      if (data.senderId && data.senderId !== 'AI_AGENT') {
        senderIds.add(data.senderId);
      }

      // Track message types
      const type = data.type || 'text';
      messageTypes[type] = (messageTypes[type] || 0) + 1;

      // Track recent activity (last 10 messages)
      if (recentActivity.length < 10) {
        recentActivity.push({
          messageId: docSnap.id,
          senderId: data.senderId,
          senderName: data.senderName,
          timestamp: data.timestamp?.toDate?.()?.toISOString() || null,
          type: data.type
        });
      }
    });

    const stats = {
      totalMessages,
      activeParticipants: senderIds.size,
      messageTypes,
      recentActivity: recentActivity.sort((a, b) =>
        new Date(b.timestamp) - new Date(a.timestamp)
      )
    };

    console.log(`[discussionFirestoreService] Forum stats for trip ${tripId}:`, stats);
    return stats;

  } catch (error) {
    console.error('[discussionFirestoreService] Error getting forum stats:', error);
    throw error;
  }
};

/**
 * Updates proposal status for a message
 * Convenience method for accepting/rejecting AI proposals
 *
 * @param {string} userId - The effectiveUserId (trip owner for shared trips)
 * @param {string} tripId - Trip identifier
 * @param {string} messageId - Message identifier
 * @param {string} status - New status ('pending', 'accepted', 'rejected', 'processed')
 * @returns {Promise<void>}
 * @throws {Error} If update fails
 */
export const updateProposalStatus = async (userId, tripId, messageId, status) => {
  try {
    if (!userId || !tripId || !messageId || !status) {
      throw new Error('All parameters are required');
    }

    const validStatuses = ['pending', 'accepted', 'rejected', 'processed'];
    if (!validStatuses.includes(status)) {
      throw new Error(`Invalid status: ${status}. Must be one of: ${validStatuses.join(', ')}`);
    }

    await updateMessage(userId, tripId, messageId, {
      'proposal.status': status,
      'proposal.updatedAt': serverTimestamp()
    });

    console.log(`[discussionFirestoreService] Proposal status updated to ${status} for message ${messageId}`);

  } catch (error) {
    console.error('[discussionFirestoreService] Error updating proposal status:', error);
    throw error;
  }
};

// Export all functions
export default {
  postMessage,
  getMessages,
  getMessage,
  updateMessage,
  deleteMessage,
  hardDeleteMessage,
  getForumStats,
  updateProposalStatus
};
